import { useMemo } from "react";
import {
  addDays,
  endOfDay,
  endOfMonth,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { CalendarDays, ChevronLeft, ChevronRight, List, Plus, Rows3 } from "lucide-react";
import type { AgendaSettings } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import type { ViewMode } from "@/lib/agenda-navigation";
import { rotuloDoPeriodo } from "@/lib/dashboard-week";
import { MobileMonthView } from "./MobileMonthView";
import { MobileWeekView } from "./MobileWeekView";
import { MobileDayView } from "./MobileDayView";
import { useOcupaRodape } from "@/components/ajuda/RodapeOcupado";
import { ALTURA_DA_BARRA } from "@/components/layout/mobile/BarraDeAbas";

type Props = {
  anchorDate: Date;
  viewMode: ViewMode;
  settings: AgendaSettings;
  changeViewMode: (m: ViewMode) => void;
  navigate: (direction: 1 | -1) => void;
  /** Compromissos de OUTRAS organizações (Agenda Pessoal) — mesclados na mesma lista, sempre
   *  só-leitura (ver `organization` em cada item). */
  extraAppointments?: any[];
  /** Se false, omite os compromissos da própria organização ativa. Default true. */
  includeOwn?: boolean;
  /** O segundo argumento é o compromisso completo — quem embute (Início) abre o detalhe com ele,
   *  sem refazer a busca; a página Agenda continua usando só o id. */
  onAppointmentClick?: (id: string, appointment?: any) => void;
  onNewAppointment?: () => void;
  /** Embutido em outra página (Início): sem seletor de visão, sem FAB e sem rolagem interna —
   *  quem controla visão/altura é a página hospedeira; aqui fica só cabeçalho ‹ período › + grade. */
  embutido?: boolean;
};

export function MobileAgenda({
  anchorDate,
  viewMode,
  settings,
  changeViewMode,
  navigate,
  extraAppointments,
  includeOwn = true,
  onAppointmentClick,
  onNewAppointment,
  embutido = false,
}: Props) {
  const ocupaRodape = useOcupaRodape<HTMLButtonElement>();
  const range = useMemo(() => {
    if (viewMode === "month") {
      return {
        rangeStart: startOfMonth(anchorDate),
        rangeEnd: endOfMonth(anchorDate),
      };
    }
    if (viewMode === "week") {
      const s = startOfWeek(anchorDate, { weekStartsOn: 0 });
      return { rangeStart: s, rangeEnd: endOfDay(addDays(s, 6)) };
    }
    // Day: cobre o dia inteiro (00:00 → 23:59:59) pra não perder
    // consultas conforme o horário atual do anchorDate.
    return {
      rangeStart: startOfDay(anchorDate),
      rangeEnd: endOfDay(anchorDate),
    };
  }, [anchorDate, viewMode]);

  const apptsQuery = trpc.appointment.listByRange.useQuery(range, { enabled: includeOwn });
  // `enabled: false` não esvazia o cache da query de mesma chave que a página mantém ligada — é
  // aqui que includeOwn de fato tira as consultas próprias da lista (escopo "Organizações").
  const appointments = useMemo(
    () => [...(includeOwn ? ((apptsQuery.data ?? []) as any[]) : []), ...(extraAppointments ?? [])],
    [apptsQuery.data, includeOwn, extraAppointments],
  );

  // "fortnight" é só do desktop; se vazar pra cá, o rótulo de semana é o mais próximo.
  const headerLabel = rotuloDoPeriodo(viewMode === "fortnight" ? "week" : viewMode, anchorDate);

  const aoClicar = onAppointmentClick
    ? (id: string) => onAppointmentClick(id, appointments.find((a: any) => a.id === id))
    : undefined;

  return (
    <div className={embutido ? "flex flex-col bg-white" : "flex h-full flex-col bg-white"}>
      <div className="flex items-center justify-between gap-2 bg-brand-600 px-2 py-3 text-white shadow-sm">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/15"
          aria-label="Anterior"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <div className="min-w-0 flex-1 text-center text-[17px] font-semibold">
          {headerLabel}
        </div>
        <button
          type="button"
          onClick={() => navigate(1)}
          className="flex h-9 w-9 items-center justify-center rounded-full transition-colors active:bg-white/15"
          aria-label="Proximo"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      </div>

      {!embutido && <SegmentoDaVisao viewMode={viewMode} onChange={changeViewMode} />}

      {/* Folga pros dois botões fixos empilhados (Nova consulta e, acima dele, Ajuda): o último
          horário precisa poder rolar até ficar acima dos dois. */}
      <div
        className={embutido ? "" : "flex-1 overflow-y-auto"}
        style={embutido ? undefined : { paddingBottom: "8rem" }}
      >
        {viewMode === "month" && (
          <MobileMonthView
            anchorDate={anchorDate}
            appointments={appointments}
            settings={settings}
            onAppointmentClick={aoClicar}
          />
        )}
        {viewMode === "week" && (
          <MobileWeekView
            anchorDate={anchorDate}
            appointments={appointments}
            settings={settings}
            onAppointmentClick={aoClicar}
          />
        )}
        {viewMode === "day" && (
          <MobileDayView
            date={anchorDate}
            appointments={appointments}
            settings={settings}
            onAppointmentClick={aoClicar}
          />
        )}
      </div>

      {!embutido && onNewAppointment && (
        <button
          ref={ocupaRodape}
          type="button"
          onClick={onNewAppointment}
          className="fixed right-4 z-10 flex h-12 items-center justify-center gap-1.5 rounded-full bg-brand-600 pl-4 pr-5 text-sm font-semibold text-white shadow-xl active:scale-95"
          // Acima da barra de abas do app (`ALTURA_DA_BARRA`). Com texto, não só "+": a barra de abas
          // já tem um "+" logo abaixo, e dois botões iguais empilhados confundem.
          style={{ bottom: `calc(${ALTURA_DA_BARRA} + 1rem + env(safe-area-inset-bottom))` }}
        >
          <Plus className="h-5 w-5" />
          Nova consulta
        </button>
      )}
    </div>
  );
}

/** Mês / Semana / Dia no topo da agenda: embaixo mora a barra de abas do app. */
function SegmentoDaVisao({ viewMode, onChange }: { viewMode: ViewMode; onChange: (m: ViewMode) => void }) {
  const opcoes: { modo: ViewMode; rotulo: string; icone: React.ReactNode }[] = [
    { modo: "month", rotulo: "Mês", icone: <CalendarDays className="h-4 w-4" /> },
    { modo: "week", rotulo: "Semana", icone: <Rows3 className="h-4 w-4" /> },
    { modo: "day", rotulo: "Dia", icone: <List className="h-4 w-4" /> },
  ];
  return (
    <div role="tablist" aria-label="Visão da agenda" className="grid grid-cols-3 gap-1 border-b border-zinc-200 bg-white p-1.5">
      {opcoes.map((o) => {
        const ativo = viewMode === o.modo;
        return (
          <button
            key={o.modo}
            type="button"
            role="tab"
            aria-selected={ativo}
            onClick={() => onChange(o.modo)}
            className={`flex h-9 items-center justify-center gap-1.5 rounded-md text-[13px] font-semibold transition-colors ${
              ativo ? "bg-brand-50 text-brand-700" : "text-zinc-500 active:bg-zinc-100"
            }`}
          >
            {o.icone}
            {o.rotulo}
          </button>
        );
      })}
    </div>
  );
}
